/* BDF HOME 3D CAROUSEL — canonical card, interaction and particle-depth owner. */
(()=>{
  'use strict';
  const SELECTOR='[data-bdf-home-3d-carousel]';

  function init(root){
    if(!root||root.dataset.bdf3dReady==='1')return;
    const stage=root.querySelector('.bdf-home-3d-carousel__stage');
    const cards=[...root.querySelectorAll('[data-bdf-3d-card]')];
    const previous=root.querySelector('[data-bdf-3d-prev]');
    const next=root.querySelector('[data-bdf-3d-next]');
    if(!stage||cards.length<1)return;
    root.dataset.bdf3dReady='1';

    const records=cards.map(card=>({
      card,
      front:card.querySelector('.bdf-home-3d-carousel__front'),
      product:card.querySelector('.bdf-home-3d-carousel__front')?.firstElementChild
    }));
    const count=cards.length;
    const step=360/count;
    const reduced=matchMedia('(prefers-reduced-motion:reduce)').matches;
    const mobileMq=matchMedia('(max-width:768px)');
    const MOVE_MS=800;
    const DWELL_MS=2400;
    const RESUME_AFTER_HOVER_MS=650;
    const DRAG_THRESHOLD=6;
    const PARTICLE_DEPTH_BIAS=8;
    const PARTICLE_DEPTH_CLEARANCE=3;
    const dragBlockSelector='button,input,select,textarea,label,[role="button"]';

    let mode=null;
    let position=0;
    let dragging=false;
    let dragMoved=false;
    let suppressClick=false;
    let startX=0;
    let startPosition=0;
    let frame=0;
    let animation=null;
    let hovered=null;
    let metrics={};
    let mobileTrack=null;
    let mobileToken=0;
    let nextAutoAt=0;

    const ease=value=>1-Math.pow(1-value,3);
    const radians=degrees=>degrees*Math.PI/180;
    const wrap=value=>((value%count)+count)%count;
    const normalize=degrees=>((degrees%360)+540)%360-180;
    const primaryIndex=()=>((Math.round(position)%count)+count)%count;

    function readMetrics(){
      const style=getComputedStyle(root);
      metrics={
        radiusX:parseFloat(style.getPropertyValue('--bdf-3d-radius-x'))||790,
        depth:parseFloat(style.getPropertyValue('--bdf-3d-depth'))||660,
        dragPixels:parseFloat(style.getPropertyValue('--bdf-3d-drag-px'))||320,
        perspective:parseFloat(style.perspective)||2350
      };
    }

    function clearParticleGeometry(){
      stage.style.removeProperty('--bdf-particle-stack-z');
      stage.style.removeProperty('--bdf-particle-depth-z3d');
      stage.style.removeProperty('--bdf-particle-home-scale');
      delete root.dataset.bdfParticleStackZ;
      delete root.dataset.bdfParticleDepthZ;
      delete root.dataset.bdfParticleScale;
      delete root.dataset.bdfParticleFrontCards;
      delete root.dataset.bdfParticleFrontCount;
    }

    function render(){
      if(mode!=='desktop')return;
      const primary=primaryIndex();
      const layers=cards.map((card,index)=>{
        const degrees=normalize((index-position)*step);
        const theta=radians(degrees);
        const cosine=Math.cos(theta);
        const frontness=(cosine+1)/2;
        const rear=Math.abs(degrees)>90;
        const x=Math.sin(theta)*metrics.radiusX;
        const z=(cosine-1)*metrics.depth;
        const scale=.79+frontness*.21;
        let brightness=.48+frontness*.52;
        if(rear)brightness=Math.max(brightness,.66);
        const active=index===primary;
        const hoverScale=card===hovered&&active?1.025:1;
        return{card,index,degrees,frontness,rear,x,z,scale,brightness,active,hoverScale,stack:0,rank:0};
      });

      const ranked=[...layers].sort((a,b)=>
        b.frontness-a.frontness||
        Math.abs(a.degrees)-Math.abs(b.degrees)||
        a.degrees-b.degrees||
        a.index-b.index
      );
      ranked.forEach((layer,rank)=>{
        layer.rank=rank+1;
        layer.stack=4000-rank*20;
      });

      const third=ranked[2]||ranked[ranked.length-1];
      const fourth=ranked[3]||ranked[ranked.length-1];
      const midpointDepth=(third.z+fourth.z)/2-PARTICLE_DEPTH_BIAS;
      const particleDepth=midpointDepth>=fourth.z?
        Math.max(midpointDepth,fourth.z+PARTICLE_DEPTH_CLEARANCE):
        Math.min(midpointDepth,fourth.z-PARTICLE_DEPTH_CLEARANCE);
      const fourthInFront=particleDepth<fourth.z;
      const particleStack=fourthInFront?
        fourth.stack-1:
        Math.floor((third.stack+fourth.stack)/2);
      const stageWidth=Math.max(1,stage.clientWidth||340);
      const stageHeight=Math.max(1,stage.clientHeight||500);
      const rootWidth=Math.max(1,root.clientWidth||stageWidth);
      const rootHeight=Math.max(1,root.clientHeight||stageHeight);
      const localBase=Math.min(1080,stageWidth*.63,stageHeight*1.11);
      const targetBase=Math.min(1120,rootWidth*.70,rootHeight*1.42);
      const projection=metrics.perspective/Math.max(1,metrics.perspective-particleDepth);
      const particleScale=targetBase/Math.max(1,localBase*projection);

      layers.forEach(layer=>{
        const{card,degrees,rear,x,z,scale,brightness,active,hoverScale,stack,rank}=layer;
        card.style.transform=`translate3d(${x.toFixed(2)}px,0,${z.toFixed(3)}px) rotateY(${degrees.toFixed(3)}deg) scale(${(scale*hoverScale).toFixed(4)})`;
        card.style.opacity='1';
        card.style.filter=`brightness(${brightness.toFixed(3)})`;
        card.style.pointerEvents=active?'auto':'none';
        card.style.zIndex=String(stack);
        card.dataset.bdf3dAngle=degrees.toFixed(3);
        card.dataset.bdf3dDepth=z.toFixed(3);
        card.dataset.bdf3dRank=String(rank);
        card.classList.toggle('is-active',active);
        card.classList.toggle('is-rear',rear);
      });

      const frontCount=Math.min(ranked.length,fourthInFront?4:3);
      stage.style.setProperty('--bdf-particle-stack-z',String(particleStack));
      stage.style.setProperty('--bdf-particle-depth-z3d',`${particleDepth.toFixed(3)}px`);
      stage.style.setProperty('--bdf-particle-home-scale',particleScale.toFixed(6));
      root.dataset.bdfParticleStackZ=String(particleStack);
      root.dataset.bdfParticleDepthZ=particleDepth.toFixed(3);
      root.dataset.bdfParticleScale=particleScale.toFixed(6);
      root.dataset.bdfParticleFrontCards=ranked.slice(0,frontCount).map(layer=>layer.index).join(',');
      root.dataset.bdfParticleFrontCount=String(frontCount);
    }

    function animateTo(target,duration=MOVE_MS){
      if(mode!=='desktop')return;
      hovered=null;
      cards.forEach(card=>card.classList.remove('is-hovered'));
      animation={from:position,to:target,start:performance.now(),duration};
      nextAutoAt=Infinity;
    }

    function stepBy(delta){animateTo(Math.round(position)+delta)}

    function tick(now){
      if(mode!=='desktop')return;
      if(animation){
        const progress=Math.min(1,(now-animation.start)/animation.duration);
        position=animation.from+(animation.to-animation.from)*ease(progress);
        if(progress>=1){
          position=wrap(animation.to);
          animation=null;
          nextAutoAt=now+DWELL_MS;
        }
      }else if(!dragging&&!hovered&&!reduced&&now>=nextAutoAt){
        animateTo(Math.round(position)+1);
      }
      render();
      frame=requestAnimationFrame(tick);
    }

    function startDesktop(){
      cancelAnimationFrame(frame);
      readMetrics();
      position=wrap(Math.round(position));
      animation=null;
      nextAutoAt=performance.now()+DWELL_MS;
      render();
      frame=requestAnimationFrame(tick);
    }

    function stopDesktop(){
      cancelAnimationFrame(frame);
      frame=0;
      animation=null;
      hovered=null;
      dragging=false;
      dragMoved=false;
      suppressClick=false;
      clearParticleGeometry();
      cards.forEach(card=>card.classList.remove('is-hovered','is-active','is-rear'));
    }

    function buildMobileTrack(){
      if(mobileTrack)return mobileTrack;
      mobileTrack=document.createElement('div');
      mobileTrack.className='bdf-home-3d-carousel__mobile slick-slider product-list-slider';
      mobileTrack.setAttribute('data-bdf-mobile-native','1');
      records.forEach(({product})=>{if(product)mobileTrack.appendChild(product)});
      root.appendChild(mobileTrack);
      return mobileTrack;
    }

    function initMobileSlick(token){
      const track=buildMobileTrack();
      const attempt=()=>{
        if(mode!=='mobile'||token!==mobileToken)return;
        const $=window.jQuery;
        if(!$||!$.fn||typeof $.fn.slick!=='function'){
          setTimeout(attempt,50);
          return;
        }
        const $track=$(track);
        if($track.hasClass('slick-initialized'))return;
        const slides=Math.max(1,parseInt(root.dataset.bdfMobileSlides||'1',10));
        const center=String(root.dataset.bdfMobileCenter)==='true';
        $track.slick({
          slidesToShow:slides,
          slidesToScroll:1,
          infinite:true,
          arrows:true,
          dots:false,
          centerMode:center,
          adaptiveHeight:false,
          swipe:true,
          swipeToSlide:true,
          touchMove:true,
          draggable:true,
          waitForAnimate:false,
          touchThreshold:5,
          speed:300
        });
      };
      attempt();
    }

    function teardownMobile(){
      if(!mobileTrack)return;
      const $=window.jQuery;
      if($&&$.fn&&typeof $.fn.slick==='function'&&$(mobileTrack).hasClass('slick-initialized'))$(mobileTrack).slick('unslick');
      records.forEach(({front,product})=>{if(front&&product)front.appendChild(product)});
      mobileTrack.remove();
      mobileTrack=null;
    }

    function activateMobile(){
      if(mode==='mobile')return;
      stopDesktop();
      mode='mobile';
      mobileToken++;
      root.classList.add('is-mobile-native');
      root.removeAttribute('tabindex');
      cards.forEach(card=>{
        card.style.cssText='';
        card.classList.remove('is-rear','is-active','is-hovered');
        card.removeAttribute('data-bdf3d-angle');
        card.removeAttribute('data-bdf3d-depth');
        card.removeAttribute('data-bdf3d-rank');
      });
      initMobileSlick(mobileToken);
    }

    function activateDesktop(){
      if(mode==='desktop')return;
      mobileToken++;
      teardownMobile();
      mode='desktop';
      root.classList.remove('is-mobile-native');
      root.setAttribute('tabindex','0');
      startDesktop();
    }

    function switchMode(){mobileMq.matches?activateMobile():activateDesktop()}

    function pointerDown(event){
      if(mode!=='desktop'||event.target.closest(dragBlockSelector))return;
      if(event.pointerType==='mouse'&&event.button!==0)return;
      dragging=true;
      dragMoved=false;
      suppressClick=false;
      animation=null;
      startX=event.clientX;
      startPosition=position;
      nextAutoAt=Infinity;
    }

    function pointerMove(event){
      if(mode!=='desktop'||!dragging)return;
      const delta=event.clientX-startX;
      if(!dragMoved&&Math.abs(delta)<DRAG_THRESHOLD)return;
      if(!dragMoved){
        dragMoved=true;
        root.classList.add('is-dragging');
        try{root.setPointerCapture?.(event.pointerId)}catch{}
      }
      if(event.cancelable)event.preventDefault();
      position=startPosition-delta/metrics.dragPixels;
      render();
    }

    function pointerUp(event){
      if(mode!=='desktop'||!dragging)return;
      const moved=dragMoved;
      dragging=false;
      dragMoved=false;
      root.classList.remove('is-dragging');
      try{root.releasePointerCapture?.(event.pointerId)}catch{}
      if(moved){
        suppressClick=true;
        setTimeout(()=>{suppressClick=false},0);
        animateTo(Math.round(position),Math.min(MOVE_MS,420+Math.abs(Math.round(position)-position)*500));
      }else{
        nextAutoAt=performance.now()+DWELL_MS;
      }
    }

    function clickCapture(event){
      if(mode!=='desktop'||!suppressClick)return;
      suppressClick=false;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    }

    function dragStart(event){
      if(mode==='desktop'&&event.target.closest('a[href],img'))event.preventDefault();
    }

    function enterCard(event){
      if(mode!=='desktop')return;
      const card=event.currentTarget;
      if(!card.classList.contains('is-active'))return;
      if(animation){animation=null;nextAutoAt=Infinity}
      hovered=card;
      card.classList.add('is-hovered');
      render();
    }

    function leaveCard(event){
      if(mode!=='desktop')return;
      const card=event.currentTarget;
      card.classList.remove('is-hovered');
      if(hovered===card)hovered=null;
      if(!dragging)nextAutoAt=performance.now()+RESUME_AFTER_HOVER_MS;
      render();
    }

    function keyDown(event){
      if(mode!=='desktop')return;
      if(event.key==='ArrowLeft'){
        event.preventDefault();
        stepBy(-1);
      }else if(event.key==='ArrowRight'){
        event.preventDefault();
        stepBy(1);
      }
    }

    const resizeHandler=()=>{
      if(mode==='desktop'){
        readMetrics();
        render();
      }
    };
    const previousClick=()=>stepBy(-1);
    const nextClick=()=>stepBy(1);

    cards.forEach(card=>{
      card.addEventListener('pointerenter',enterCard);
      card.addEventListener('pointerleave',leaveCard);
    });
    root.addEventListener('pointerdown',pointerDown,true);
    window.addEventListener('pointermove',pointerMove,{capture:true,passive:false});
    window.addEventListener('pointerup',pointerUp,true);
    window.addEventListener('pointercancel',pointerUp,true);
    root.addEventListener('click',clickCapture,true);
    root.addEventListener('dragstart',dragStart,true);
    root.addEventListener('keydown',keyDown);
    previous?.addEventListener('click',previousClick);
    next?.addEventListener('click',nextClick);
    addEventListener('resize',resizeHandler,{passive:true});
    mobileMq.addEventListener?.('change',switchMode);
    switchMode();

    root._bdf3dDestroy=()=>{
      stopDesktop();
      mobileToken++;
      teardownMobile();
      mode=null;
      cards.forEach(card=>{
        card.removeEventListener('pointerenter',enterCard);
        card.removeEventListener('pointerleave',leaveCard);
        card.style.cssText='';
        card.removeAttribute('data-bdf3d-angle');
        card.removeAttribute('data-bdf3d-depth');
        card.removeAttribute('data-bdf3d-rank');
      });
      root.removeEventListener('pointerdown',pointerDown,true);
      window.removeEventListener('pointermove',pointerMove,true);
      window.removeEventListener('pointerup',pointerUp,true);
      window.removeEventListener('pointercancel',pointerUp,true);
      root.removeEventListener('click',clickCapture,true);
      root.removeEventListener('dragstart',dragStart,true);
      root.removeEventListener('keydown',keyDown);
      previous?.removeEventListener('click',previousClick);
      next?.removeEventListener('click',nextClick);
      removeEventListener('resize',resizeHandler);
      mobileMq.removeEventListener?.('change',switchMode);
      root.classList.remove('is-mobile-native','is-dragging');
      clearParticleGeometry();
      delete root.dataset.bdf3dReady;
    };
  }

  function boot(scope=document){scope.querySelectorAll(SELECTOR).forEach(init)}
  function restoreFromBfcache(){
    document.querySelectorAll(SELECTOR).forEach(root=>root._bdf3dDestroy?.());
    boot(document);
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>boot(),{once:true});
  else boot();
  document.addEventListener('shopify:section:load',event=>boot(event.target));
  addEventListener('pageshow',event=>{if(event.persisted)restoreFromBfcache()});
})();